/**
 * Jabatan Table Component
 * 
 * Displays a table of jabatan (positions).
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, Edit, Trash2, Briefcase, Users, Building2, MapPin, ArrowUpDown } from "lucide-react";
import type { Jabatan } from "@/services/managementService";
import { cn } from "@/lib/utils";

interface JabatanTableProps {
  jabatan: Jabatan[];
  loading: boolean;
  onEdit: (jabatan: Jabatan) => void;
  onDelete: (jabatan: Jabatan) => void;
}

export function JabatanTable({ jabatan, loading, onEdit, onDelete }: JabatanTableProps) {
  const [sortBy, setSortBy] = React.useState<"name" | "level" | "order">("order");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");

  const getLevelLabel = (level: string) => {
    switch (level) {
      case "nasional":
        return "Nasional";
      case "province":
        return "Provinsi";
      case "city":
        return "Kota/Kabupaten";
      default:
        return level;
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case "nasional":
        return <Users className="h-3 w-3" />;
      case "province":
        return <Building2 className="h-3 w-3" />;
      case "city":
        return <MapPin className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case "nasional":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 border-blue-200 dark:border-blue-800";
      case "province":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border-green-200 dark:border-green-800";
      case "city":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 border-purple-200 dark:border-purple-800";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 border-gray-200 dark:border-gray-800";
    }
  };

  // Sort jabatan
  const sortedJabatan = React.useMemo(() => {
    const sorted = [...jabatan];
    sorted.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      if (sortBy === "name") {
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
      } else if (sortBy === "level") {
        aVal = a.level;
        bVal = b.level;
      } else {
        aVal = a.order;
        bVal = b.order;
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [jabatan, sortBy, sortOrder]);

  const handleSort = (field: "name" | "level" | "order") => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Daftar Jabatan
          </CardTitle>
          <CardDescription>
            Memuat data jabatan...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (jabatan.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Daftar Jabatan
          </CardTitle>
          <CardDescription>
            Total: 0 jabatan
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-full bg-muted p-6">
                <Briefcase className="h-12 w-12 text-muted-foreground" />
              </div>
              <div>
                <p className="text-lg font-medium">Tidak ada data jabatan</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Mulai dengan menambahkan jabatan baru
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Daftar Jabatan
            </CardTitle>
            <CardDescription className="mt-1">
              Total: {jabatan.length} jabatan {jabatan.length !== sortedJabatan.length ? `(${sortedJabatan.length} ditampilkan)` : ""}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border overflow-x-auto shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="min-w-[180px]">
                  <Button
                    variant="ghost"
                    className="h-8 px-2 hover:bg-transparent font-semibold"
                    onClick={() => handleSort("name")}
                  >
                    Nama Jabatan
                    {sortBy === "name" && (
                      <ArrowUpDown className={cn("ml-2 h-3 w-3", sortOrder === "desc" && "rotate-180")} />
                    )}
                  </Button>
                </TableHead>
                <TableHead className="min-w-[250px]">Deskripsi</TableHead>
                <TableHead className="min-w-[140px]">
                  <Button
                    variant="ghost"
                    className="h-8 px-2 hover:bg-transparent font-semibold"
                    onClick={() => handleSort("level")}
                  >
                    Level
                    {sortBy === "level" && (
                      <ArrowUpDown className={cn("ml-2 h-3 w-3", sortOrder === "desc" && "rotate-180")} />
                    )}
                  </Button>
                </TableHead>
                <TableHead className="min-w-[100px]">
                  <Button
                    variant="ghost"
                    className="h-8 px-2 hover:bg-transparent font-semibold"
                    onClick={() => handleSort("order")}
                  >
                    Urutan
                    {sortBy === "order" && (
                      <ArrowUpDown className={cn("ml-2 h-3 w-3", sortOrder === "desc" && "rotate-180")} />
                    )}
                  </Button>
                </TableHead>
                <TableHead className="min-w-[120px] text-center">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedJabatan.map((item, index) => (
                <TableRow
                  key={item._id}
                  className={cn(
                    "hover:bg-muted/50 transition-colors",
                    index % 2 === 0 && "bg-background"
                  )}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{item.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {item.description || (
                        <span className="italic text-muted-foreground/70">Tidak ada deskripsi</span>
                      )}
                    </p>
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border",
                        getLevelColor(item.level)
                      )}
                    >
                      {getLevelIcon(item.level)}
                      {getLevelLabel(item.level)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium">{item.order}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(item)}
                        className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                        title="Edit jabatan"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(item)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                        title="Hapus jabatan"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

